<template>
  <div class="text-stone-700 flex flex-col gap-4">
    <!-- Gift card section -->
    <div v-if="showGiftcard" class="giftcard-container">
      <span
        class="absolute top-0 right-3 -translate-y-1/2 bg-teal-500 text-white text-xs font-semibold px-2.5 py-0.5 rounded-full"
      >
        {{ t('product.show.noDates.giftCard.badge') }}
      </span>

      <div class="flex items-start justify-between gap-2">
        <div>
          <span class="font-bold text-body-xl leading-snug">
            {{ t('product.show.noDates.giftCard.title') }}
          </span>
          <p class="text-sm text-gray-500 mt-0.5">
            {{ t('product.show.noDates.giftCard.subtitle') }}
          </p>
        </div>
        <span class="font-bold text-body-xl shrink-0">{{
          formattedPrice
        }}</span>
      </div>

      <div class="mt-3 bg-teal-50 border border-teal-200 rounded-2xl p-3">
        <div
          class="flex items-center gap-1.5 text-xs font-semibold text-teal-700 uppercase tracking-wide mb-2"
        >
          <Gift class="w-3.5 h-3.5" />
          {{ t('product.show.noDates.giftCard.sectionLabel') }}
        </div>
        <ul class="flex flex-col gap-1.5 text-xs text-gray-700">
          <li class="flex gap-2">
            <Check class="w-4 h-4 text-teal-600 shrink-0" />
            {{ t('product.show.noDates.giftCard.bullet1') }}
          </li>
          <li class="flex gap-2">
            <Check class="w-4 h-4 text-teal-600 shrink-0" />
            {{ t('product.show.noDates.giftCard.bullet2') }}
          </li>
          <li class="flex gap-2">
            <Check class="w-4 h-4 text-teal-600 shrink-0" />
            {{ t('product.show.noDates.giftCard.bullet3') }}
          </li>
        </ul>
      </div>

      <Button
        data-test-id="buy-box-no-dates-giftcard-cta"
        class="w-full mt-4 py-3 rounded-xl"
        :style="buttonStyle"
        @click="buyGiftcard"
      >
        {{ t('product.show.checkout.bookGiftcard') }}
      </Button>
    </div>

    <!-- OR divider -->
    <div v-if="showGiftcard" class="flex items-center gap-2">
      <hr class="flex-1 border-dashed border-gray-200" />
      <span class="text-xs text-gray-400 uppercase tracking-wide">{{
        t('product.show.noDates.or')
      }}</span>
      <hr class="flex-1 border-dashed border-gray-200" />
    </div>

    <!-- Request a date section -->
    <div class="request-container flex flex-col gap-2">
      <div class="flex items-start justify-between gap-2">
        <span class="font-bold text-body-xl">
          {{ t('product.show.noDates.dateRequest.title') }}
        </span>
        <span
          class="bg-lime-200 text-lime-900 text-xs font-semibold px-3 py-1 rounded-full shrink-0"
        >
          {{ t('product.show.noDates.dateRequest.badge') }}
        </span>
      </div>
      <p class="text-sm text-gray-500">
        {{ t('product.show.noDates.dateRequest.description') }}
      </p>
      <Button
        data-test-id="buy-box-no-dates-request-date-button"
        variant="outlined"
        class="w-full py-3 flex items-center justify-center gap-2 border-2 font-semibold rounded-xl"
        @click="requestDate"
      >
        <Calendar class="w-4 h-4" />
        {{ t('product.show.noAvailableDatesBox.ctaTextOnlyProposeDate') }}
      </Button>
    </div>

    <!-- Notify section -->
    <div class="flex flex-col gap-2">
      <template v-if="!subscribed">
        <div
          class="flex items-center justify-center gap-2 text-sm font-medium text-gray-700"
        >
          <Bell class="w-4 h-4 text-teal-600" />
          {{ t('product.show.noDates.notify.title') }}
        </div>
        <div
          class="flex items-center border rounded-2xl p-1 pr-2 py-2 transition-colors focus-within:border-teal-600"
          :class="emailError ? 'border-red-400' : 'border-gray-200'"
        >
          <input
            v-model="email"
            type="email"
            :placeholder="t('product.show.noDates.notify.placeholder')"
            class="flex-1 min-w-0 px-3 py-1.5 text-sm outline-none bg-transparent"
            @input="emailError = false"
            @keydown.enter="subscribe"
          />
          <Button
            data-test-id="buy-box-no-dates-notify-button"
            variant="contained"
            class="py-1.5 px-4 shrink-0 rounded-xl text-sm"
            @click="subscribe"
          >
            {{ t('product.show.noDates.notify.cta') }}
          </Button>
        </div>
        <p
          v-if="emailError"
          class="text-red-500 text-xs flex items-center gap-1"
        >
          <InfoCircleIcon class="w-3.5 h-3.5 shrink-0" />
          {{ t('product.show.noDates.notify.emailError') }}
        </p>
      </template>

      <div
        v-else
        class="flex flex-col items-center gap-2 bg-teal-50 border border-teal-200 rounded-2xl px-4 py-5 text-center"
      >
        <span class="flex items-center gap-2">
          <CheckCircle class="w-5 h-5 text-teal-600 shrink-0" />
          <p class="font-semibold text-sm text-stone-700">
            {{ t('product.show.noDates.notify.successTitle') }}
          </p>
        </span>
        <p class="text-sm text-gray-500">
          {{ t('product.show.noDates.notify.successBody') }}
        </p>
      </div>

      <p class="text-xs text-center text-gray-400">
        {{ t('product.show.noDates.notify.subtext') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Bell, Calendar, Check, CheckCircle, Gift } from 'lucide-vue-next'
import parseGiftcardToCartItem from '~/utils/parseGiftcardToCartItem'
import type { CartItem } from '~/composables/types/cart'
import type { EventDescription } from '~/composables/types/event'
import { useAlgoliaConversion } from '~/composables/algolia/useAlgoliaConversion'
import InfoCircleIcon from '~/assets/icons/info-circle.svg?component'

const { showGiftcard, isEmbedded, backgroundColor, textColor } = defineProps<{
  showGiftcard: boolean
  isEmbedded?: boolean
  backgroundColor?: string
  textColor?: string
}>()

const emit = defineEmits<{
  (e: 'add-item-to-cart', cartItem: CartItem): void
}>()

const eventData = inject<EventDescription>('eventData')!
const isSpecialGiftcardEvent = inject('isSpecialGiftcardEvent') as boolean
const { pricingsFactory } = usePricingsFactory()
const pricings = pricingsFactory(
  Number(
    eventData.retail_price?.amount ||
      eventData.original_price?.amount ||
      eventData.default_price.amount
  ),
  { totalParticipants: 1 }
)
const { locale, t } = useI18n()
const route = useRoute()
const { addItem, clear: clearCart } = useCartWithSubdomainLogic()
const { toggleCartSidebar, toggleDateRequestSidebar } = useUiState()
const { sendAddToCartEvent } = useAlgoliaConversion()

const email = ref('')
const emailError = ref(false)
const subscribed = ref(false)

const formattedPrice = computed(() => {
  const amount = Number(
    eventData.retail_price?.amount ||
      eventData.original_price?.amount ||
      eventData.default_price.amount
  )
  return formatMoney(
    amount / 100,
    convertLocaleIntoBCP47(locale.value),
    'EUR',
    Number.isInteger(amount / 100) ? 0 : 2
  )
})

const buttonStyle = computed(() => ({
  ...(textColor ? { color: `#${textColor}` } : {}),
  ...(backgroundColor ? { backgroundColor: `#${backgroundColor}` } : {}),
}))

const buyGiftcard = async () => {
  const cartItem = parseGiftcardToCartItem(
    eventData,
    1,
    ref(undefined),
    pricings,
    isSpecialGiftcardEvent ? 'SPECIAL' : 'EVENT'
  )
  if (cartItem) {
    emit('add-item-to-cart', cartItem)
    if (isEmbedded) clearCart()
    await addItem(cartItem)
    toggleCartSidebar()
    sendAddToCartEvent(eventData)
  }
}

const requestDate = () => {
  toggleDateRequestSidebar(route.name, 'buybox_no_dates')
}

const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)

const subscribe = async () => {
  if (!isValidEmail(email.value)) {
    emailError.value = true
    return
  }
  try {
    const response = await kftApiRequest(
      `v1/store/events/${eventGetters.getId(eventData)}/subscribe`,
      { body: { email: email.value } }
    )
    subscribed.value = true
    sendWaitlistForm(response?.data?.sha256_email_address)
  } catch {
    emailError.value = true
  }
}
</script>
<style scoped lang="postcss">
.giftcard-container {
  @apply relative border border-teal-600/10 rounded-2xl p-4;
  box-shadow: 0px 2px 12px 0px #047a881a;
}

.request-container {
  @apply relative border border-gray-50 rounded-2xl p-4;
  box-shadow: 0px 2px 12px 0px #0000000f;
}
</style>
